/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  Input,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';

import {
  DialogRef,
  DialogService,
  ButtonComponent,
  ConfirmDialogComponent,
  AlertComponent,
  IconComponent,
  FormLayoutComponent,
  FormFieldDirective,
  NebulaLayoutBranch,
  TableCompactComponent,
  TableCompactCellDirective,
  TableCompactConfig,
  TableCompactChange,
  TableCompactActionEvent,
  NebulaSearchCriteria,
} from 'nebula-ui-kit';

import { BuscadorMaestroService } from '../../data-access/buscador-maestro/buscador-maestro.service';
import { PlanCuentasService } from '../../data-access/plan-cuentas-modal/accounting-plan.service';
import { BuscadorMaestroConfig } from '../buscador-maestro/buscador-maestro.models';
import {
  CentroCostoModalService,
  CentroCostoSelect,
} from '../../data-access/centro-costos-modal/centro-costos-modal.service';
import { PlanCuenta } from '../../domain/plan-cuentas-modal.models';
import { SystemParametersDto } from '@nebula-shared/domain';
import {
  COMMON,
  ServiceSystemParametersDate,
  TranslatePipe,
  TranslationService,
  TREASURY,
} from '@nebula-shared/data-access';
import { distribucionDelDiferidoDialogo } from './diferidos-modal.layout';

export interface CentroCosto {
  codigo: string;
  nombre: string;
  porcentaje: number;
  esNuevo?: boolean;
  id?: number;
}

export interface DiferidoData {
  valorDiferido: number;
  fechaRegistro: string;
  fechaInicial: string;
  fechaFinal: string;
  diasDiferidos: number;
  periodosDiferir: number;
  codCuenta: string;
  nombreCuenta: string;
  baseCalculo: string;
  centrosCosto: CentroCosto[];
}

function notEmptyValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return { required: true };
  }
  if (typeof value === 'number' && Number.isNaN(value)) {
    return { required: true };
  }
  return null;
}

function fechaValidaValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return { fechaInvalida: true };
  }
  return null;
}

@Component({
  selector: 'app-diferido-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    AlertComponent,
    IconComponent,
    FormLayoutComponent,
    FormFieldDirective,
    TableCompactComponent,
    TranslatePipe,
    TableCompactCellDirective,
    MatTooltipModule,
  ],
  templateUrl: './diferidos-modal.component.html',
  styleUrls: ['./diferidos-modal.component.scss'],
})
export class DiferidoDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(DialogRef, { optional: true });
  readonly dialogService = inject(DialogService);
  readonly fb = inject(FormBuilder);
  readonly cdr = inject(ChangeDetectorRef);
  readonly viewContainerRef = inject(ViewContainerRef);
  private cuentaSeleccionadaId: number | null = null;

  readonly buscador = inject(BuscadorMaestroService);
  readonly planCuentas = inject(PlanCuentasService);
  readonly centroCostosModalService = inject(CentroCostoModalService);
  readonly systemParametersService = inject(ServiceSystemParametersDate);

  @Input() esCuentaDebito = false;
  @Input() esCuentaCredito = false;
  @Input() cuentaOriginalId?: number;
  @Input() naturalezaCuentaOriginal?: string;
  @Input() totalAntesImpuesto = 0;
  @Input() fechaExpedicion?: string;
  @Output() saved = new EventEmitter<DiferidoData>();

  loading = signal(false);
  loadingTable = signal(false);
  readonly destroy$ = new Subject<void>();

  mostrarAlerta = signal(false);
  alertaTipo = signal<'success' | 'error' | 'warning' | 'info'>('info');
  alertaTitulo = signal('');
  alertaMensaje = signal('');

  private readonly translation = inject(TranslationService);

  baseCalculoOptions = [
    { value: '360', label: this.t('ACCOUNTING.DIFERIDO_DIALOG.BASE_360') },
    { value: '365', label: this.t('ACCOUNTING.DIFERIDO_DIALOG.BASE_365') },
  ];

  /** Rangos del calendario como señales: el motor los lee cuando cambian. */
  private readonly minInicio = signal<Date | null>(null);
  private readonly maxInicio = signal<Date | null>(null);
  private readonly minFin = signal<Date | null>(null);
  /** Filas de la grilla de centros, como señal para que la grilla se repinte. */
  readonly filasDeCentros = signal<CentroCosto[]>([]);

  /** La distribucion de la ventana, armada una vez: lo que cambia lo leen sus señales. */
  readonly layout: NebulaLayoutBranch = distribucionDelDiferidoDialogo({
    bases: this.baseCalculoOptions,
    buscarCuenta: {
      icono: 'Search',
      tooltipKey: 'ACCOUNTING.DIFERIDO_DIALOG.TOOLTIP.ACCOUNT_CODE',
      al: () => this.buscarCuenta(),
      soloPorAccion: true,
    },
    minInicio: this.minInicio,
    maxInicio: this.maxInicio,
    minFin: this.minFin,
  });

  /** Como se nombra el error de un campo. Se entrega al motor UNA vez. */
  protected readonly errorDelCampo = (name: string): string => this.getError(name);

  /** Falla de una fila de la distribucion: con centro, el porcentaje va de 0 a 100. */
  errorDelCentro(row: CentroCosto): string | null {
    if (!row.codigo) return null;
    const p = row.porcentaje as unknown;
    if (p === null || p === undefined || p === '') return this.t('COMMON.VALIDATION.REQUIRED');
    const n = Number(p);
    if (Number.isNaN(n) || n < 0)
      return this.t('ACCOUNTING.DEFERRED_PROCESS.COST_CENTER.ERROR.MIN');
    if (n > 100) return this.t('ACCOUNTING.DEFERRED_PROCESS.COST_CENTER.ERROR.MAX');
    return null;
  }

  /** Distribucion por centro de costo con la grilla editable del ERP; el total va al pie. */
  readonly tablaDeCentros = computed<TableCompactConfig<CentroCosto>>(() => ({
    title: 'ACCOUNTING.DIFERIDO_DIALOG.COST_CENTERS',
    icon: 'Users',
    counter: true,
    columns: [
      {
        key: 'codigo',
        label: 'ACCOUNTING.DIFERIDO_DIALOG.COLUMN.CODE',
        type: 'text',
        placeholder: 'ACCOUNTING.DIFERIDO_DIALOG.COLUMN.CODE',
        width: '66%',
        readonly: this.loading(),
      },
      {
        key: 'porcentaje',
        label: 'ACCOUNTING.DIFERIDO_DIALOG.COLUMN.PERCENTAGE',
        type: 'number',
        placeholder: '0',
        width: '16%',
        align: 'right',
        suffix: '%',
        readonly: this.loading(),
      },
    ],
    actions: [
      { id: 'add', label: 'ACCOUNTING.DIFERIDO_DIALOG.ADD', icon: 'Plus', encabezado: true },
      { id: 'delete', label: 'ACTIONS.DELETE', icon: 'Trash2' },
    ],
    rowError: (row: CentroCosto) => this.errorDelCentro(row),
    summary: [
      {
        label: 'ACCOUNTING.DIFERIDO_DIALOG.TOTAL',
        value: `${this.sumaPorcentajes()} %`,
        type: 'text',
        total: true,
      },
    ],
  }));

  /** Una celda cambio: el porcentaje viaja como numero. */
  alCambiarCentro(cambio: TableCompactChange<CentroCosto>): void {
    const valor = (cambio.row as unknown as Record<string, unknown>)[cambio.column];
    const numero =
      valor === '' || valor === null || valor === undefined
        ? null
        : Number(String(valor).replace(',', '.'));
    this.onCentrosCostoDataChange(
      this.centrosCostoData.map((f, i) =>
        i === cambio.index ? { ...f, [cambio.column]: numero } : f,
      ),
    );
  }

  /** Accion de la grilla: agregar desde la cabecera o eliminar la fila. */
  alAccionarCentro(evento: TableCompactActionEvent): void {
    if (evento.action === 'add') this.agregarFilaCentroCosto();
    if (evento.action === 'delete' && evento.index !== undefined) {
      this.eliminarFilaCentroCosto(evento.index);
    }
  }

  /** Lupa del centro de una fila. */
  /**
   * Lupa de la celda: abre su buscador en esa fila.
   *
   * @param index la fila
   */
  buscarEnLaFila(index: number): void {
    const row = this.filasDeCentros()[index];
    if (row) this.alBuscarCentro({ row, column: 'codigo', index });
  }

  alBuscarCentro(busqueda: TableCompactChange<CentroCosto>): void {
    if (busqueda.column === 'codigo') this.buscarCentroCostoParaTabla(busqueda.index);
  }

  /** Vuelca los rangos de fechas y las filas en sus señales. */
  private reflejar(): void {
    this.minInicio.set(this.fechaMinimaInicial ?? null);
    this.maxInicio.set(this.fechaMaxima ?? null);
    this.minFin.set(this.fechaMinimaFinal ?? null);
    this.filasDeCentros.set([...this.centrosCostoData]);
  }

  protected t(key: string): string {
    return this.translation.translate(key);
  }

  centrosCostoData: any[] = [];
  sumaPorcentajes = signal(0);

  systemParameters: SystemParametersDto | null = null;
  manejaDiferidos = signal(false);

  fechaMinimaInicial: Date | undefined = undefined;
  fechaMinimaFinal: Date | undefined = undefined;
  fechaMaxima: Date | undefined = undefined;

  diferidoForm: FormGroup;

  get valorDiferidoControl(): FormControl {
    return this.diferidoForm.get('valorDiferido') as FormControl;
  }

  get fechaRegistroControl(): FormControl {
    return this.diferidoForm.get('fechaRegistro') as FormControl;
  }

  get fechaInicialControl(): FormControl {
    return this.diferidoForm.get('fechaInicial') as FormControl;
  }

  get fechaFinalControl(): FormControl {
    return this.diferidoForm.get('fechaFinal') as FormControl;
  }

  get diasDiferidosControl(): FormControl {
    return this.diferidoForm.get('diasDiferidos') as FormControl;
  }

  get periodosDiferirControl(): FormControl {
    return this.diferidoForm.get('periodosDiferir') as FormControl;
  }

  get codCuentaControl(): FormControl {
    return this.diferidoForm.get('codCuenta') as FormControl;
  }

  get nombreCuentaControl(): FormControl {
    return this.diferidoForm.get('nombreCuenta') as FormControl;
  }

  get baseCalculoControl(): FormControl {
    return this.diferidoForm.get('baseCalculo') as FormControl;
  }

  constructor() {
    this.diferidoForm = this.fb.group({
      valorDiferido: [null, [Validators.required, Validators.min(0.01), notEmptyValidator]],
      fechaRegistro: [{ value: this.obtenerFechaActual(), disabled: true }],
      fechaInicial: ['', [Validators.required, notEmptyValidator, fechaValidaValidator]],
      fechaFinal: ['', [Validators.required, notEmptyValidator, fechaValidaValidator]],
      diasDiferidos: [
        { value: 0, disabled: true },
        [Validators.max(99999), Validators.pattern(/^\d+$/)],
      ],
      periodosDiferir: [{ value: 0, disabled: true }],
      codCuenta: ['', [Validators.required, Validators.maxLength(20)]],
      nombreCuenta: ['', [Validators.maxLength(100)]],
      baseCalculo: ['', [Validators.required, notEmptyValidator]],
    });

    // La cuenta destino no se teclea: la llena su buscador (soloPorAccion), que necesita el
    // control habilitado. El nombre solo se lee.
    this.nombreCuentaControl.disable();
  }

  async ngOnInit(): Promise<void> {
    await this.cargarParametrosSistema();
    this.cargarValoresPorDefecto();
    this.centrosCostoData = [];
    this.valorDiferidoControl.valueChanges.subscribe(() => {
      this.validarValorDiferido();
    });
    // El calendario del motor no emite su propio evento: las fechas se siguen por su valor.
    this.fechaInicialControl.valueChanges.subscribe((v) => {
      this.onFechaInicialChange(v);
      this.reflejar();
    });
    this.fechaFinalControl.valueChanges.subscribe((v) => this.onFechaFinalChange(v));
    // La fecha inicial puede venir precargada (la expedicion): su piso para la final ya aplica.
    this.actualizarFechaMinimaFinal();
    this.reflejar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private configurarRangosFechas(): void {
    if (!this.systemParameters?.fechaSistema) return;

    const fechaSistema = this.parseFechaSistema(this.systemParameters.fechaSistema);
    if (!fechaSistema) return;

    this.fechaMinimaInicial = new Date(fechaSistema);

    const anioSiguiente = fechaSistema.getFullYear() + 1;
    this.fechaMaxima = new Date(anioSiguiente, 11, 31);

    this.actualizarFechaMinimaFinal();
    this.reflejar();
  }

  private actualizarFechaMinimaFinal(): void {
    const fechaInicialValue = this.fechaInicialControl.value;
    if (fechaInicialValue) {
      const fechaInicialDate = this.parseFecha(fechaInicialValue);
      if (fechaInicialDate) {
        this.fechaMinimaFinal = new Date(fechaInicialDate);
        this.fechaMinimaFinal.setDate(this.fechaMinimaFinal.getDate() + 1);
      }
    } else {
      this.fechaMinimaFinal = undefined;
    }
  }

  private validarFechaInicialContraFechaSistema(fechaInicial: Date): boolean {
    if (!this.systemParameters?.fechaSistema) {
      return true;
    }

    const fechaSistema = this.parseFechaSistema(this.systemParameters.fechaSistema);
    if (!fechaSistema) return true;

    const fechaInicialComparar = new Date(fechaInicial);
    fechaInicialComparar.setHours(0, 0, 0, 0);
    const fechaSistemaComparar = new Date(fechaSistema);
    fechaSistemaComparar.setHours(0, 0, 0, 0);

    if (fechaInicialComparar < fechaSistemaComparar) {
      this.mostrarAlertaError(
        `La fecha inicial no puede ser menor a la fecha del sistema (${this.formatearFecha(fechaSistema)})`,
      );
      return false;
    }
    return true;
  }

  private validarAnioVigencia(fecha: Date, campo: 'inicial' | 'final'): boolean {
    if (!this.systemParameters?.fechaSistema) {
      return true;
    }

    const fechaSistema = this.parseFechaSistema(this.systemParameters.fechaSistema);
    if (!fechaSistema) return true;

    const anioActual = fechaSistema.getFullYear();
    const anioSiguiente = anioActual + 1;
    const anioFecha = fecha.getFullYear();

    if (anioFecha !== anioActual && anioFecha !== anioSiguiente) {
      this.mostrarAlertaError(
        `La fecha ${campo === 'inicial' ? 'inicial' : 'final'} solo puede estar en el año ${anioActual} o ${anioSiguiente}`,
      );
      return false;
    }
    return true;
  }

  private validarFechaFinalContraFechaSistema(fechaFinal: Date): boolean {
    if (!this.systemParameters?.fechaSistema) {
      return true;
    }

    const fechaSistema = this.parseFechaSistema(this.systemParameters.fechaSistema);
    if (!fechaSistema) return true;

    const fechaFinalComparar = new Date(fechaFinal);
    fechaFinalComparar.setHours(0, 0, 0, 0);
    const fechaSistemaComparar = new Date(fechaSistema);
    fechaSistemaComparar.setHours(0, 0, 0, 0);

    if (fechaFinalComparar > fechaSistemaComparar) {
      this.mostrarAlertaError(
        `La fecha final no puede ser mayor a la fecha del sistema (${this.formatearFecha(fechaSistema)})`,
      );
      return false;
    }
    return true;
  }

  private parseFechaSistema(fechaStr: string | Date): Date | null {
    if (!fechaStr) return null;

    if (fechaStr instanceof Date) {
      const fecha = new Date(fechaStr);
      fecha.setHours(0, 0, 0, 0);
      return fecha;
    }

    if (typeof fechaStr === 'string') {
      if (fechaStr.includes('-')) {
        const partes = fechaStr.split('-');
        if (partes.length === 3) {
          const fecha = new Date(
            parseInt(partes[0], 10),
            parseInt(partes[1], 10) - 1,
            parseInt(partes[2], 10),
          );
          fecha.setHours(0, 0, 0, 0);
          return fecha;
        }
      } else if (fechaStr.includes('/')) {
        const partes = fechaStr.split('/');
        if (partes.length === 3) {
          const fecha = new Date(
            parseInt(partes[2], 10),
            parseInt(partes[1], 10) - 1,
            parseInt(partes[0], 10),
          );
          fecha.setHours(0, 0, 0, 0);
          return fecha;
        }
      }
    }

    return null;
  }

  onCentrosCostoDataChange(data: any[]): void {
    this.centrosCostoData = data;
    this.calcularSumaPorcentajesDesdeData();
    this.actualizarValidacionFormulario();
    this.cdr.detectChanges();
  }
  private async cargarParametrosSistema(): Promise<void> {
    try {
      this.systemParameters = await firstValueFrom(this.systemParametersService.getParametros());
      this.manejaDiferidos.set(this.systemParameters?.manejaDiferidos ?? false);

      if (this.systemParameters?.fechaSistema) {
        const fechaSistema = this.formatearFechaDesdeString(this.systemParameters.fechaSistema);
        this.fechaRegistroControl.setValue(fechaSistema);
        this.configurarRangosFechas();
      } else {
        this.fechaRegistroControl.setValue(this.obtenerFechaActual());
      }

      if (!this.manejaDiferidos()) {
        this.mostrarAlertaError(
          'El sistema no está configurado para manejar diferidos. Por favor, active la opción "maneja diferidos" en los parámetros del sistema.',
        );
        setTimeout(() => {
          this.dialogRef?.close(null);
        }, 2000);
      }
    } catch (error) {
      this.fechaRegistroControl.setValue(this.obtenerFechaActual());
      this.mostrarAlertaError('No se pudo verificar la configuración de diferidos del sistema');
    }
  }

  private formatearFechaDesdeString(fechaStr: string | Date): string {
    if (!fechaStr) return this.obtenerFechaActual();

    let fecha: Date;

    if (fechaStr instanceof Date) {
      fecha = fechaStr;
    } else if (typeof fechaStr === 'string') {
      if (fechaStr.includes('-')) {
        const partes = fechaStr.split('-');
        if (partes.length === 3) {
          fecha = new Date(
            parseInt(partes[0], 10),
            parseInt(partes[1], 10) - 1,
            parseInt(partes[2], 10),
          );
        } else {
          fecha = new Date(fechaStr);
        }
      } else if (fechaStr.includes('/')) {
        const partes = fechaStr.split('/');
        if (partes.length === 3) {
          fecha = new Date(
            parseInt(partes[2], 10),
            parseInt(partes[1], 10) - 1,
            parseInt(partes[0], 10),
          );
        } else {
          fecha = new Date(fechaStr);
        }
      } else {
        fecha = new Date(fechaStr);
      }
    } else {
      fecha = new Date();
    }

    if (isNaN(fecha.getTime())) {
      return this.obtenerFechaActual();
    }

    return this.formatearFecha(fecha);
  }

  private calcularSumaPorcentajesDesdeData(): void {
    let suma = 0;
    for (const row of this.centrosCostoData) {
      const porcentaje = row.porcentaje;
      if (porcentaje !== null && porcentaje !== undefined && porcentaje !== '') {
        const num = Number(porcentaje);
        if (!isNaN(num)) {
          suma += num;
        }
      }
    }
    this.sumaPorcentajes.set(suma);
    this.filasDeCentros.set([...this.centrosCostoData]);
    this.verificarHabilitarBoton();
  }

  private verificarHabilitarBoton(): void {
    if (this.codCuentaControl.value && !this.codCuentaControl.disabled) {
      this.codCuentaControl.markAsTouched();
    }
    this.cdr.detectChanges();
  }

  buscarCentroCostoParaTabla(index: number): void {
    const currentRow = this.centrosCostoData[index];

    this.centroCostosModalService
      .openSelector('Seleccionar Centro de Costo', currentRow?.codigo || '')
      .subscribe({
        next: (selected: CentroCostoSelect | null) => {
          if (selected) {
            const updatedData = [...this.centrosCostoData];
            updatedData[index] = {
              ...updatedData[index],
              codigo: selected.codigo,
              nombre: selected.nombre,
              id: selected.id,
              porcentaje: updatedData[index]?.porcentaje || null,
            };
            this.centrosCostoData = updatedData;
            this.calcularSumaPorcentajesDesdeData();
            this.actualizarValidacionFormulario();
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          this.mostrarAlertaError('Error al buscar centro de costo');
        },
      });
  }

  agregarFilaCentroCosto(): void {
    const hayFilaVacia = this.centrosCostoData.some(
      (row) => !row.codigo || row.codigo.trim() === '',
    );

    if (hayFilaVacia) {
      this.mostrarAlertaError('Complete o elimine las filas vacías antes de agregar una nueva');
      return;
    }

    this.centrosCostoData = [
      ...this.centrosCostoData,
      { codigo: '', nombre: '', porcentaje: null },
    ];
    this.calcularSumaPorcentajesDesdeData();
    this.actualizarValidacionFormulario();
  }

  eliminarFilaCentroCosto(rowIndex: number): void {
    if (this.centrosCostoData.length === 1) {
      this.centrosCostoData = [{ codigo: '', nombre: '', porcentaje: null }];
    } else {
      const newData = [...this.centrosCostoData];
      newData.splice(rowIndex, 1);
      this.centrosCostoData = newData;
    }
    this.calcularSumaPorcentajesDesdeData();
    this.actualizarValidacionFormulario();
  }

  private obtenerFechaActual(): string {
    const hoy = new Date();
    return this.formatearFecha(hoy);
  }

  private formatearFecha(fecha: Date): string {
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const anio = fecha.getFullYear();
    return `${dia}/${mes}/${anio}`;
  }

  private parseFecha(fechaStr: string | Date | null): Date | null {
    if (!fechaStr) return null;
    if (fechaStr instanceof Date) return fechaStr;
    if (typeof fechaStr === 'string') {
      const partes = fechaStr.split('/');
      if (partes.length === 3) {
        const dia = parseInt(partes[0], 10);
        const mes = parseInt(partes[1], 10) - 1;
        const anio = parseInt(partes[2], 10);
        return new Date(anio, mes, dia);
      }
      const fecha = new Date(fechaStr);
      if (!isNaN(fecha.getTime())) {
        return fecha;
      }
    }
    return null;
  }

  private cargarValoresPorDefecto(): void {
    if (this.fechaExpedicion) {
      this.fechaInicialControl.setValue(this.fechaExpedicion);
    }
  }

  onFechaInicialChange(event: any): void {
    this.actualizarFechaMinimaFinal();
    this.calcularDiasYPeriodos();
    this.validarFechaInicialConParametros();
  }

  onFechaFinalChange(event: any): void {
    this.calcularDiasYPeriodos();
    this.validarFechaFinalConParametros();
  }

  private validarFechaInicialConParametros(): void {
    const fechaInicialStr = this.fechaInicialControl.value;
    if (!fechaInicialStr) return;

    const fechaInicial = this.parseFecha(fechaInicialStr);
    if (!fechaInicial) return;

    if (!this.validarFechaInicialContraFechaSistema(fechaInicial)) {
      this.fechaInicialControl.setErrors({ fechaInvalida: true });
      return;
    }

    if (!this.validarAnioVigencia(fechaInicial, 'inicial')) {
      this.fechaInicialControl.setErrors({ fechaInvalida: true });
      return;
    }

    if (this.fechaInicialControl.hasError('fechaInvalida')) {
      this.fechaInicialControl.setErrors(null);
    }
  }

  private validarFechaFinalConParametros(): void {
    const fechaFinalStr = this.fechaFinalControl.value;
    if (!fechaFinalStr) return;

    const fechaFinal = this.parseFecha(fechaFinalStr);
    if (!fechaFinal) return;

    if (!this.validarAnioVigencia(fechaFinal, 'final')) {
      this.fechaFinalControl.setErrors({ fechaInvalida: true });
      return;
    }

    if (this.fechaFinalControl.hasError('fechaInvalida')) {
      this.fechaFinalControl.setErrors(null);
    }
  }

  validarValorDiferido(): void {
    const valor = this.valorDiferidoControl.value;
    if (valor !== null && valor !== undefined && valor !== '') {
      const valorNum = Number(valor);
      if (valorNum <= 0) {
        this.valorDiferidoControl.setErrors({ min: { min: 0.01, actual: valorNum } });
        this.mostrarAlertaError('El valor diferido debe ser mayor a 0');
      } else if (valorNum > this.totalAntesImpuesto) {
        this.valorDiferidoControl.setErrors({
          max: { max: this.totalAntesImpuesto, actual: valorNum },
        });
        this.mostrarAlertaError(
          `El valor diferido no puede superar el Total Antes Impuesto (${this.formatCurrency(this.totalAntesImpuesto)})`,
        );
      }
    }
  }

  calcularDiasYPeriodos(): void {
    const fechaInicialStr = this.fechaInicialControl.value;
    const fechaFinalStr = this.fechaFinalControl.value;

    if (!fechaInicialStr || !fechaFinalStr) {
      this.diasDiferidosControl.setValue(0);
      this.periodosDiferirControl.setValue(0);
      return;
    }

    const fechaInicial = this.parseFecha(fechaInicialStr);
    const fechaFinal = this.parseFecha(fechaFinalStr);

    if (!fechaInicial || !fechaFinal) {
      this.diasDiferidosControl.setValue(0);
      this.periodosDiferirControl.setValue(0);
      return;
    }

    if (fechaFinal <= fechaInicial) {
      this.fechaFinalControl.setErrors({ fechaInvalida: true });
      this.mostrarAlertaError('La fecha final debe ser mayor a la fecha inicial');
      this.diasDiferidosControl.setValue(0);
      this.periodosDiferirControl.setValue(0);
      return;
    }

    if (this.fechaFinalControl.hasError('fechaInvalida')) {
      this.fechaFinalControl.setErrors(null);
    }

    const diffTime = fechaFinal.getTime() - fechaInicial.getTime();
    let diffDias = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    diffDias = Math.max(1, Math.floor(diffDias));

    const MAX_DIAS = 99999;
    if (diffDias > MAX_DIAS) {
      this.mostrarAlertaError(
        `Los días diferidos no pueden superar los ${MAX_DIAS.toLocaleString()} días`,
      );
      this.diasDiferidosControl.setValue(MAX_DIAS);
      this.diasDiferidosControl.setErrors({ max: { max: MAX_DIAS, actual: diffDias } });
    } else {
      this.diasDiferidosControl.setValue(diffDias);
      if (this.diasDiferidosControl.hasError('max')) {
        this.diasDiferidosControl.setErrors(null);
      }
    }

    const anioInicial = fechaInicial.getFullYear();
    const mesInicial = fechaInicial.getMonth();
    const anioFinal = fechaFinal.getFullYear();
    const mesFinal = fechaFinal.getMonth();

    const periodos = (anioFinal - anioInicial) * 12 + (mesFinal - mesInicial) + 1;
    this.periodosDiferirControl.setValue(periodos);

    this.actualizarValidacionFormulario();
  }

  private validarCentrosCostoDuplicados(): boolean {
    const codigos = new Set<string>();
    let duplicado = false;

    for (const row of this.centrosCostoData) {
      const codigo = row.codigo;
      if (codigo && codigo.trim() !== '') {
        if (codigos.has(codigo)) {
          this.mostrarAlertaError(`El centro de costo ${codigo} está duplicado`);
          duplicado = true;
          break;
        }
        codigos.add(codigo);
      }
    }

    return !duplicado;
  }

  buscarCuenta(): void {
    let naturalezaFiltro: 'DEBITO' | 'CREDITO' | undefined = undefined;

    if (this.esCuentaDebito) {
      naturalezaFiltro = 'CREDITO';
    } else if (this.esCuentaCredito) {
      naturalezaFiltro = 'DEBITO';
    }

    // La naturaleza contraria a la de la cuenta del documento, cuando la hay.
    const porNaturaleza: NebulaSearchCriteria[] = naturalezaFiltro
      ? [{ key: 'naturaleza', operation: 'EQUAL', value: naturalezaFiltro }]
      : [];

    this.buscador
      .abrir({
        tituloKey: TREASURY.DEDUCTIONS.MODALS.CUENTA_CONTABLE.TITLE,
        endpoint: this.planCuentas.rutaDePageResponse,
        idKey: 'id',
        columnas: [
          {
            key: 'codigo',
            labelKey: COMMON.COLUMNS.CODE,
            sortable: true,
            width: '30%',
            match: 'MATCH_START',
          },
          { key: 'nombre', labelKey: COMMON.COLUMNS.NAME, sortable: true, width: '70%' },
        ],
        base: {
          searchsBy: [
            { key: 'estadoCuentaContable', operation: 'EQUAL', value: 'ACTIVO' },
            { key: 'movimiento', operation: 'EQUAL', value: 'S' },
            ...porNaturaleza,
          ],
          ordersBy: [{ column: 'codigo', direction: 'ASC' }],
        },
      } satisfies BuscadorMaestroConfig<Record<string, unknown>>)
      .subscribe({
        next: (elegidos) => {
          const selected = elegidos?.[0] as PlanCuenta | undefined;
          if (selected) {
            this.codCuentaControl.setValue(selected.codigo);
            this.nombreCuentaControl.setValue(selected.nombre);
            this.cuentaSeleccionadaId = selected.id;
            this.codCuentaControl.updateValueAndValidity();
            this.cdr.markForCheck();
            this.verificarHabilitarBoton();

            this.actualizarValidacionFormulario();
          }
        },
        error: (error) => {
          this.mostrarAlertaError('Error al buscar la cuenta contable');
        },
      });
  }

  getError(fieldName: string): string {
    const control = this.diferidoForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['min']) return `El valor mínimo es ${control.errors['min'].min}`;
    if (control.errors['max']) return `El valor máximo es ${control.errors['max'].max}`;
    if (control.errors['maxlength'])
      return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
    if (control.errors['fechaInvalida'])
      return control.errors['fechaInvalida'] === true
        ? 'Fecha inválida'
        : control.errors['fechaInvalida'];

    return 'Campo inválido';
  }

  async onSave(): Promise<void> {
    if (!this.manejaDiferidos()) {
      this.mostrarAlertaError('El sistema no está configurado para manejar diferidos');
      return;
    }

    this.validarValorDiferido();

    Object.keys(this.diferidoForm.controls).forEach((key) => {
      const control = this.diferidoForm.get(key);
      if (control && !control.disabled) {
        control.markAsTouched();
      }
    });

    const rawValue = this.diferidoForm.getRawValue();
    if (!rawValue.codCuenta || rawValue.codCuenta.trim() === '') {
      this.mostrarAlertaError('Debe seleccionar una cuenta contable');
      this.codCuentaControl.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    if (this.diferidoForm.invalid) {
      this.mostrarAlertaError('Por favor complete todos los campos requeridos correctamente');
      this.cdr.markForCheck();
      return;
    }

    if (this.sumaPorcentajes() !== 100) {
      this.mostrarAlertaError(
        `La suma de porcentajes debe ser 100%. Actual: ${this.sumaPorcentajes()}%`,
      );
      return;
    }

    if (!this.validarCentrosCostoDuplicados()) {
      return;
    }

    for (const row of this.centrosCostoData) {
      if (row.codigo && row.codigo.trim() !== '' && (!row.porcentaje || row.porcentaje <= 0)) {
        this.mostrarAlertaError(`El centro de costo ${row.codigo} debe tener un porcentaje válido`);
        return;
      }
    }

    this.loading.set(true);

    try {
      const formValue = this.diferidoForm.getRawValue();

      const fechaInicialFormateada = this.formatearFechaYYYYMMDD(formValue.fechaInicial);
      const fechaFinalFormateada = this.formatearFechaYYYYMMDD(formValue.fechaFinal);
      const fechaRegistroFormateada = this.formatearFechaYYYYMMDD(formValue.fechaRegistro);

      const centrosCostoConId = this.obtenerCentrosCostoValidosConId();

      const resultado: any = {
        valorDiferido: Number(formValue.valorDiferido),
        fechaRegistro: fechaRegistroFormateada,
        fechaInicial: fechaInicialFormateada,
        fechaFinal: fechaFinalFormateada,
        diasDiferidos: formValue.diasDiferidos,
        periodosDiferir: formValue.periodosDiferir,
        codCuenta: formValue.codCuenta?.trim() || '',
        nombreCuenta: formValue.nombreCuenta?.trim() || '',
        baseCalculo: formValue.baseCalculo,
        centrosCosto: centrosCostoConId,
        cuentaId: this.cuentaSeleccionadaId,
      };

      this.mostrarAlertaExito('Diferido guardado correctamente');

      setTimeout(() => {
        this.saved.emit(resultado);
        this.dialogRef?.close(resultado);
      }, 500);
    } catch (error) {
      this.mostrarAlertaError('Error al guardar el diferido');
    } finally {
      this.loading.set(false);
    }
  }

  private formatearFechaYYYYMMDD(fecha: string | Date): string {
    if (!fecha) return '';

    let fechaObj: Date;

    if (fecha instanceof Date) {
      fechaObj = fecha;
    } else if (typeof fecha === 'string') {
      if (fecha.includes('/')) {
        const partes = fecha.split('/');
        if (partes.length === 3) {
          fechaObj = new Date(
            parseInt(partes[2], 10),
            parseInt(partes[1], 10) - 1,
            parseInt(partes[0], 10),
          );
        } else {
          fechaObj = new Date(fecha);
        }
      } else {
        fechaObj = new Date(fecha);
      }
    } else {
      return '';
    }

    const year = fechaObj.getFullYear();
    const month = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const day = String(fechaObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private obtenerCentrosCostoValidosConId(): CentroCosto[] {
    const validos: CentroCosto[] = [];
    for (const row of this.centrosCostoData) {
      if (row.codigo && row.codigo.trim() !== '' && row.porcentaje && row.porcentaje > 0) {
        validos.push({
          codigo: row.codigo,
          nombre: row.nombre,
          porcentaje: Number(row.porcentaje),
          id: row.id || undefined,
        });
      }
    }
    return validos;
  }

  async onCancel(): Promise<void> {
    if (this.diferidoForm.dirty) {
      const confirmed = await this.showConfirmDialog();
      if (!confirmed) {
        return;
      }
    }
    this.dialogRef?.close(null);
  }

  private async showConfirmDialog(): Promise<boolean> {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: '¿Cancelar registro?',
        message: 'Se va a cancelar el registro del diferido',
        type: 'warning',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
      },
      size: 'sm',
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result === true;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  private mostrarAlertaExito(mensaje: string): void {
    this.alertaTipo.set('success');
    this.alertaTitulo.set('Éxito');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);

    setTimeout(() => {
      this.cerrarAlerta();
    }, 3000);
  }

  private mostrarAlertaError(mensaje: string): void {
    this.alertaTipo.set('error');
    this.alertaTitulo.set('Error');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  isFormularioValido(): boolean {
    if (this.diferidoForm.invalid) {
      return false;
    }

    const rawValue = this.diferidoForm.getRawValue();
    if (!rawValue.codCuenta || rawValue.codCuenta.trim() === '') {
      return false;
    }

    const valorDiferido = this.valorDiferidoControl.value;
    if (!valorDiferido || valorDiferido <= 0) {
      return false;
    }

    const fechaInicial = this.fechaInicialControl.value;
    if (!fechaInicial) {
      return false;
    }

    const fechaFinal = this.fechaFinalControl.value;
    if (!fechaFinal) {
      return false;
    }

    const baseCalculo = this.baseCalculoControl.value;
    if (!baseCalculo) {
      return false;
    }

    const diasDiferidos = this.diasDiferidosControl.value;
    if (diasDiferidos === null || diasDiferidos === undefined || diasDiferidos <= 0) {
      return false;
    }

    if (!Number.isInteger(diasDiferidos)) {
      return false;
    }

    const MAX_DIAS = 99999;
    if (diasDiferidos > MAX_DIAS) {
      return false;
    }

    if (this.centrosCostoData && this.centrosCostoData.length > 0) {
      const tieneCentrosValidos = this.centrosCostoData.some(
        (row) => row.codigo && row.codigo.trim() !== '' && row.porcentaje && row.porcentaje > 0,
      );

      if (!tieneCentrosValidos) {
        return false;
      }

      if (this.sumaPorcentajes() !== 100) {
        return false;
      }

      if (!this.validarCentrosCostoDuplicados()) {
        return false;
      }
    }

    return true;
  }

  isFormularioBasicoValido(): boolean {
    return this.diferidoForm.valid;
  }

  private actualizarValidacionFormulario(): void {
    this.cdr.detectChanges();

    // Se revalida SIN emitir: las fechas se siguen por su valor y emitir aqui las volveria a
    // disparar sin fin.
    Object.keys(this.diferidoForm.controls).forEach((key) => {
      const control = this.diferidoForm.get(key);
      if (control) {
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
    this.cdr.markForCheck();
  }

  cerrarAlerta(): void {
    this.mostrarAlerta.set(false);
  }
}
